/*
 * Todas las páginas se cargan de forma diferida: Vue Router acepta como `component`
 * una función que devuelva una Promise, así que cada `() => import(...)` se convierte
 * en un chunk aparte que solo se descarga al entrar en la ruta.
 *
 * Con imports estáticos las 42 páginas caían en el bundle de entrada, y quien abría
 * /login se descargaba además el Playground, el Catálogo, ag-grid y echarts.
 *
 * El `webpackChunkName` agrupa a propósito las páginas que se visitan juntas (las
 * pestañas de Settings, las de Skills...) para no convertir una navegación en cinco
 * peticiones. Al cambiar estos nombres se cambia el reparto de chunks.
 */

// Auth Pages
const Login = () => import(/* webpackChunkName: "auth" */ '../pages/Auth/Login.vue');
const Register = () => import(/* webpackChunkName: "auth" */ '../pages/Auth/Register.vue');
const ForgotPassword = () => import(/* webpackChunkName: "auth" */ '../pages/Auth/ForgotPassword.vue');
const Invite = () => import(/* webpackChunkName: "auth" */ '../pages/Auth/Invite.vue');
const PendingInvitation = () => import(/* webpackChunkName: "auth" */ '@/pages/Auth/PendingInvitation.vue');
const Onboard = () => import(/* webpackChunkName: "auth" */ '../pages/Auth/OnboardingPage.vue');
const NotFound = () => import(/* webpackChunkName: "auth" */ '../pages/Common/NotFound.vue');
const LinkExpired = () => import(/* webpackChunkName: "auth" */ '../pages/Common/LinkExpired.vue');

const DashboardLayout = () => import(/* webpackChunkName: "layout" */ '../pages/Dashboard/Layout/DashboardLayout.vue');

const ChatPage = () => import(/* webpackChunkName: "chat" */ '../pages/Dashboard/Conversations/ChatPage.vue');

const SettingsLayout = () => import(/* webpackChunkName: "settings" */ '../pages/Dashboard/Settings/SettingsLayout.vue');
const AccountSettings = () => import(/* webpackChunkName: "settings" */ '../pages/Dashboard/Settings/Account/AccountSettings.vue');
const ApiKeys = () => import(/* webpackChunkName: "settings" */ '../pages/Dashboard/Settings/apiKeys/ApiKeys.vue');
const UsersManagement = () => import(/* webpackChunkName: "settings" */ '../pages/Dashboard/Settings/UsersManagement/UsersManagement.vue');
const Subscription = () => import(/* webpackChunkName: "settings" */ '../pages/Dashboard/Settings/Subscription/Subscription.vue');
const NotificationsSettings = () => import(/* webpackChunkName: "settings" */ '../pages/Dashboard/Settings/Notifications/NotificationsSettings.vue');
const ToursSettings = () => import(/* webpackChunkName: "settings" */ '../pages/Dashboard/Settings/Tours/ToursSettings.vue');
const BlacklistPage = () => import(/* webpackChunkName: "settings" */ '@/pages/Dashboard/Settings/Blacklist/BlacklistPage.vue');

// import Sales from '../pages/Dashboard/Sales/Sales.vue';

const Playground = () => import(/* webpackChunkName: "playground" */ '../pages/Dashboard/Playground/Playground.vue');
const Training = () => import(/* webpackChunkName: "training" */ '../pages/Dashboard/Training/Training.vue');
const Catalog = () => import(/* webpackChunkName: "catalog" */ '../pages/Dashboard/Training/CatalogPage.vue');
const Campaigns = () => import(/* webpackChunkName: "campaigns" */ '../pages/Dashboard/Campaigns/Campaigns.vue');
const PipelinePage = () => import(/* webpackChunkName: "pipeline" */ '@/pages/Dashboard/Pipeline/PipelinePage.vue');

const Skills = () => import(/* webpackChunkName: "skills" */ '@/pages/Dashboard/Skills/SkillsPage.vue');
const SkillsDiscoveryTab = () => import(/* webpackChunkName: "skills" */ '@/pages/Dashboard/Skills/SkillsDiscoveryTab.vue');
const SkillsMySkillsTab = () => import(/* webpackChunkName: "skills" */ '@/pages/Dashboard/Skills/SkillsMySkillsTab.vue');
const SkillsDetails = () => import(/* webpackChunkName: "skills" */ '@/pages/Dashboard/Skills/SkillsDetailsPage.vue');

const IntegrationsPage = () => import(/* webpackChunkName: "integrations" */ '@/pages/Dashboard/Integrations/IntegrationsPage.vue');
const IntegrationsDiscoveryTab = () => import(/* webpackChunkName: "integrations" */ '@/pages/Dashboard/Integrations/IntegrationsDiscoveryTab.vue');
const IntegrationsMyIntegrationsTab = () => import(/* webpackChunkName: "integrations" */ '@/pages/Dashboard/Integrations/IntegrationsMyIntegrationsTab.vue');

const CallsPage = () => import(/* webpackChunkName: "calls" */ '@/pages/Dashboard/Calls/CallsPage.vue');
const CustomersPage = () => import(/* webpackChunkName: "customers" */ '@/pages/Dashboard/Customers/CustomersPage.vue');
const CustomerDetailsPage = () => import(/* webpackChunkName: "customers" */ '@/pages/Dashboard/Customers/CustomerDetailsPage.vue');
const AudiencesPage = () => import(/* webpackChunkName: "customers" */ '@/pages/Dashboard/Customers/AudiencesPage.vue');
const AudienceDetails = () => import(/* webpackChunkName: "customers" */ '@/pages/Dashboard/Customers/AudienceDetails.vue');
const AudienceCreatorPage = () => import(/* webpackChunkName: "customers" */ '@/pages/Dashboard/Customers/AudienceCreatorPage.vue');

// Estas tres arrastran echarts (~7 MB de módulos), que así deja de entrar en el bundle inicial.
const MainDashboardPage = () => import(/* webpackChunkName: "dashboarding" */ '@/pages/Dashboard/Dashboarding/Main/MainDashboardPage.vue');
const GettingStarted = () => import(/* webpackChunkName: "dashboarding" */ '@/pages/Dashboard/Dashboard/GettingStarted.vue');
const Analytics = () => import(/* webpackChunkName: "dashboarding" */ '@/pages/Dashboard/Analytics/Analytics.vue');

const AgentSettingsPage = () => import(/* webpackChunkName: "agent-settings" */ '@/components/AgentSettings/AgentSettingsPage.vue');
const AgentProfilesPage = () => import(/* webpackChunkName: "agent-settings" */ '@/pages/Dashboard/AgentProfiles/AgentProfilesPage.vue');
const AgentProfileDetailPage = () => import(/* webpackChunkName: "agent-settings" */ '@/pages/Dashboard/AgentProfiles/AgentProfileDetailPage.vue');

const CallDetails = () => import(/* webpackChunkName: "admin" */ '@/pages/Dashboard/Dashboarding/Voice/VoiceCallsPage.vue');
const PendingReviews = () => import(/* webpackChunkName: "admin" */ '@/pages/Dashboard/Dashboarding/PendingReviews/PendingReviewsPage.vue');
const BillingLedgerPage = () => import(/* webpackChunkName: "admin" */ '@/pages/Dashboard/Admin/BillingLedgerPage.vue');
const CampaignAutoApprovalPage = () => import(/* webpackChunkName: "admin" */ '@/pages/Dashboard/Admin/CampaignAutoApprovalPage.vue');

const linkExpiredPage = { path: '/link-expired', component: LinkExpired };

const notFoundPage = { path: '/404', component: NotFound };

const pendingInvitationPage = {
    path: '/pending-invitation',
    component: PendingInvitation,
    name: 'PendingInvitation',
    meta: { title: 'Pending Invitation' },
};

const invitePage = {
    path: '/invite',
    component: Invite,
    name: 'Invite',
    meta: { title: 'Invite' },
};

const registerPage = {
    path: '/register',
    component: Register,
    name: 'Register',
    meta: { title: 'Signup' },
};

const forgotPasswordPage = {
    path: '/forgot-password',
    component: ForgotPassword,
    name: 'Forgot Password',
    meta: { title: 'Forgot Password' },
};

const loginPage = {
    path: '/',
    component: Login,
    name: 'Login',
    meta: { title: 'Signin' },
};

const onboardPage = {
    path: '/onboard',
    component: Onboard,
    name: 'Onboard',
    meta: {
        requiresAuth: true,
        title: 'Onboarding',
        permissions: ['ADMIN', 'EMPLOYEE'],
    },
};

const dashboardPage = {
    path: '/dashboard',
    name: 'Dashboard',
    components: { default: MainDashboardPage },
    meta: {
        requiresAuth: true,
        title: 'Dashboard',
        permissions: ['ADMIN', 'EMPLOYEE'],
    },
};

const gettingStartedPage = {
    path: '/getting-started',
    name: 'Getting Started',
    components: { default: GettingStarted },
    meta: {
        requiresAuth: true,
        title: 'Getting Started',
        permissions: ['ADMIN', 'EMPLOYEE'],
    },
};

const conversationsPage = {
    path: '/conversations',
    name: 'Customer Support',
    meta: {
        requiresAuth: true,
        title: 'Customer Support',
        permissions: ['ADMIN', 'EMPLOYEE'],
    },
    components: { default: ChatPage },
};

const settingsPage = {
    path: '/settings',
    name: 'Settings',
    meta: { requiresAuth: true, title: 'Settings Overview', permissions: ['ADMIN', 'EMPLOYEE'] },
    components: { default: SettingsLayout },
    redirect: '/settings/account',
    children: [
        {
            path: 'account',
            name: 'Account Settings',
            components: { default: AccountSettings },
            meta: {
                requiresAuth: true,
                title: 'Account Settings',
                permissions: ['ADMIN', 'EMPLOYEE'],
            },
        },
        {
            path: 'api-keys',
            name: 'API Keys',
            components: { default: ApiKeys },
            meta: {
                requiresAuth: true,
                title: 'API Keys',
                permissions: ['ADMIN', 'EMPLOYEE'],
            },
        },
        {
            path: 'members',
            name: 'User Management',
            components: { default: UsersManagement },
            meta: {
                requiresAuth: true,
                title: 'User Management',
                permissions: ['ADMIN', 'EMPLOYEE'],
            },
        },
        {
            path: 'billing',
            name: 'Billing Settings',
            components: { default: Subscription },
            meta: {
                requiresAuth: true,
                title: 'Billing',
                permissions: ['ADMIN', 'EMPLOYEE'],
            },
        },
        {
            path: 'notifications',
            name: 'Notifications Settings',
            components: { default: NotificationsSettings },
            meta: {
                requiresAuth: true,
                title: 'Notifications',
                permissions: ['ADMIN', 'EMPLOYEE'],
            },
        },
        {
            path: 'tours',
            name: 'Tours',
            components: { default: ToursSettings },
            meta: {
                requiresAuth: true,
                title: 'Tours',
                permissions: ['ADMIN', 'EMPLOYEE'],
            },
        },
        {
            path: 'blacklist',
            name: 'Blacklist',
            components: { default: BlacklistPage },
        },
    ],
};

const TrainingPage = {
    path: '/agent-studio/training',
    name: 'Training Overview',
    meta: {
        requiresAuth: true,
        title: 'Training Overview',
        permissions: ['ADMIN', 'EMPLOYEE'],
    },
    components: { default: Training },
};

// Voice profiles now live inside Agent Studio Settings, as the voice tab.
// The old standalone path is kept so bookmarks and links keep working.
const voiceProfilesPage = {
    path: '/agent-studio/voice-profiles',
    redirect: { path: '/agent-studio/settings', query: { tab: 'voice' } },
};

const campaignsPage = {
    path: '/campaigns',
    name: 'Campaigns',
    meta: {
        requiresAuth: true,
        title: 'Campaigns',
        permissions: ['ADMIN', 'EMPLOYEE'],
    },
    components: { default: Campaigns },
};

const pipelinePage = {
    path: '/pipeline',
    name: 'Pipeline',
    components: { default: PipelinePage },
    meta: {
        requiresAuth: true,
        title: 'Pipeline',
        permissions: ['ADMIN', 'EMPLOYEE'],
        hideFooter: true,
    },
};

const SkillsPage = {
    path: '/agent-studio/skills',
    name: 'Skills',
    components: { default: Skills },
    meta: {
        requiresAuth: true,
        title: 'Skills Overview',
        permissions: ['ADMIN', 'EMPLOYEE'],
    },
    redirect: '/agent-studio/skills/discovery',
    children: [
        {
            path: 'discovery',
            name: 'Skills Store',
            components: { default: SkillsDiscoveryTab },
            meta: {
                requiresAuth: true,
                title: 'Skills Discovery',
                permissions: ['ADMIN', 'EMPLOYEE'],
            },
        },
        {
            path: 'my-skills',
            name: 'My Skills',
            components: { default: SkillsMySkillsTab },
            meta: {
                requiresAuth: true,
                title: 'My Skills',
                permissions: ['ADMIN', 'EMPLOYEE'],
            },
        },
    ],
};

const IntegrationsPageRouter = {
    path: '/integrations',
    name: 'Integrations',
    components: { default: IntegrationsPage },
    meta: {
        requiresAuth: true,
        title: 'Integrations Overview',
        permissions: ['ADMIN', 'EMPLOYEE'],
    },
    redirect: '/integrations/discovery',
    children: [
        {
            path: 'discovery',
            name: 'Integrations Store',
            components: { default: IntegrationsDiscoveryTab },
            meta: {
                requiresAuth: true,
                title: 'Integrations Discovery',
                permissions: ['ADMIN', 'EMPLOYEE'],
            },
        },
        {
            path: 'my-integrations',
            name: 'My Integrations',
            components: { default: IntegrationsMyIntegrationsTab },
            meta: {
                requiresAuth: true,
                title: 'My Integrations',
                permissions: ['ADMIN', 'EMPLOYEE'],
            },
        },
    ],
};

const skillDetailsPage = {
    path: '/agent-studio/skills/details/:skillId',
    name: 'Skill Details',
    components: { default: SkillsDetails },
    meta: {
        requiresAuth: true,
        title: 'Skill Details',
        permissions: ['ADMIN', 'EMPLOYEE'],
    },
};

const integrationsDetailsPage = {
    path: '/integrations/details/:skillId',
    name: 'Integration Details',
    components: { default: SkillsDetails },
    meta: {
        requiresAuth: true,
        title: 'Integration Details',
        permissions: ['ADMIN', 'EMPLOYEE'],
    },
};

const catalog = {
    path: 'catalog',
    name: 'Business Catalog',
    components: { default: Catalog },
    meta: {
        requiresAuth: true,
        title: 'Business Catalog',
        permissions: ['ADMIN', 'EMPLOYEE'],
    },
};

const playgroundPage = {
    path: '/agent',
    components: { default: Playground },
    meta: {
        requiresAuth: true,
        title: 'Agent Test Lab',
        permissions: ['ADMIN', 'EMPLOYEE'],
    },
    children: [
        {
            path: '',
            redirect: 'playground',
        },
        {
            path: 'playground',
            name: 'Playground Chat',
            meta: {
                requiresAuth: true,
                title: 'Agent Test Lab',
                permissions: ['ADMIN', 'EMPLOYEE'],
            },
        },
        {
            path: 'agent-settings',
            name: 'Agent Settings',
            meta: {
                requiresAuth: true,
                title: 'Agent Settings',
                permissions: ['ADMIN', 'EMPLOYEE'],
            },
        },
    ],
};

const analyticsPage = {
    path: 'analytics',
    name: 'Analytics',
    components: { default: Analytics },
    meta: {
        requiresAuth: true,
        title: 'Analytics',
        permissions: ['ADMIN', 'EMPLOYEE'],
    },
};

const callsPage = {
    path: '/calls',
    name: 'Calls',
    components: { default: CallsPage },
    meta: {
        requiresAuth: true,
        title: 'Calls',
        permissions: ['ADMIN', 'EMPLOYEE'],
    },
};

const customersPage = {
    path: '/customers',
    name: 'Customers',
    components: { default: CustomersPage },
    meta: {
        requiresAuth: true,
        title: 'Customers',
        permissions: ['ADMIN', 'EMPLOYEE'],
    },
};

const customerDetailsPage = {
    path: 'customers/details/:customerId',
    name: 'Customer Details',
    components: { default: CustomerDetailsPage },
    meta: {
        requiresAuth: true,
        title: 'Customer Details',
        permissions: ['ADMIN', 'EMPLOYEE'],
    },
};

const audiencesPage = {
    path: 'customers/audiences',
    name: 'Audiences',
    components: { default: AudiencesPage },
    meta: {
        requiresAuth: true,
        title: 'Audiences',
        permissions: ['ADMIN', 'EMPLOYEE'],
    },
};

const audienceCreatorPage = {
    path: 'customers/audiences/create',
    name: 'AudienceCreator',
    components: { default: AudienceCreatorPage },
    meta: {
        requiresAuth: true,
        title: 'Create Audience',
        permissions: ['ADMIN', 'EMPLOYEE'],
    },
};

const audienceDetailsPage = {
    path: 'customers/audiences/:id',
    name: 'AudienceDetails',
    components: { default: AudienceDetails },
    meta: {
        requiresAuth: true,
        title: 'Audience Details',
        permissions: ['ADMIN', 'EMPLOYEE'],
    },
};

const agentSettingsStandalonePage = {
    path: '/agent-studio/settings',
    name: 'Agent Studio Settings',
    components: { default: AgentSettingsPage },
    meta: {
        requiresAuth: true,
        title: 'Agent Settings',
        permissions: ['ADMIN', 'EMPLOYEE'],
    },
};

// Perfiles del agente: lista y detalle propios (solo equipo de Lixsa; las páginas
// redirigen a la configuración del agente si no). `default` es el agente principal.
const agentProfilesPage = {
    path: '/agent-studio/profiles',
    name: 'Agent Profiles',
    components: { default: AgentProfilesPage },
    meta: {
        requiresAuth: true,
        title: 'Agent Profiles',
        permissions: ['ADMIN', 'EMPLOYEE'],
    },
};

const agentProfileDetailPage = {
    path: '/agent-studio/profiles/:profileId',
    name: 'Agent Profile Detail',
    components: { default: AgentProfileDetailPage },
    meta: {
        requiresAuth: true,
        title: 'Agent Profile',
        permissions: ['ADMIN', 'EMPLOYEE'],
    },
};

const callDetailsPage = {
    path: '/admin/call-details',
    name: 'Call Details',
    components: { default: CallDetails },
    meta: {
        requiresAuth: true,
        title: 'Call Details',
        permissions: ['ADMIN', 'EMPLOYEE'],
    },
};

const pendingReviewsPage = {
    path: '/admin/pending-reviews',
    name: 'Pending Reviews',
    components: { default: PendingReviews },
    meta: {
        requiresAuth: true,
        title: 'Pending Reviews',
        permissions: ['ADMIN', 'EMPLOYEE'],
    },
};

const billingLedgerPage = {
    path: '/admin/billing-ledger',
    name: 'Billing Ledger',
    components: { default: BillingLedgerPage },
    meta: {
        requiresAuth: true,
        title: 'Billing Ledger',
        permissions: ['ADMIN', 'EMPLOYEE'],
    },
};

const campaignAutoApprovalPage = {
    path: '/admin/campaign-auto-approval',
    name: 'Campaign Auto Approval',
    components: { default: CampaignAutoApprovalPage },
    meta: {
        requiresAuth: true,
        title: 'Campaign Auto Approval',
        permissions: ['ADMIN', 'EMPLOYEE'],
    },
};

const baseDashboard = {
    path: '',
    component: DashboardLayout,
    name: 'Dashboard layout',
    meta: {
        requiresAuth: true,
        title: 'Dashboard',
        permissions: ['ADMIN', 'EMPLOYEE'],
    },
    children: [
        dashboardPage,
        gettingStartedPage,
        conversationsPage,
        TrainingPage,
        voiceProfilesPage,
        campaignsPage,
        pipelinePage,
        callsPage,
        customersPage,
        customerDetailsPage,
        audiencesPage,
        audienceCreatorPage,
        audienceDetailsPage,

        // SalesPage,
        settingsPage,
        SkillsPage,
        skillDetailsPage,
        integrationsDetailsPage,
        IntegrationsPageRouter,
        catalog,
        playgroundPage,
        analyticsPage,

        agentSettingsStandalonePage,
        agentProfilesPage,
        agentProfileDetailPage,
        callDetailsPage,
        pendingReviewsPage,
        billingLedgerPage,
        campaignAutoApprovalPage,
    ],
};

const routes = [
    { path: '*', redirect: '/404' },
    loginPage,
    forgotPasswordPage,
    registerPage,
    invitePage,
    pendingInvitationPage,
    notFoundPage,
    linkExpiredPage,
    onboardPage,
    baseDashboard,
];

export default routes;
